import { withApollo } from 'apollo/apollo';
import JournalPage from 'components/journal/JournalPage';
import { getLayout } from 'components/Layout/SiteLayout';
import Meta from 'components/Layout/Meta';
import { genJournalMetaData } from 'core/metaGenerators';
import { schemaMarkupGenerator } from 'core/seo';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import { handleNestedSlugs } from 'pages/[slug]';
import React from 'react';
import { Q_POST_AND_MORE_POSTS_1 } from 'src/queries/sanity';
import { Article } from 'src/types/sanity/schema';
import { getAllArticlesWithSlug, getArticleAndMoreArticles } from 'utils/sanity/api';
import { usePreviewSubscription } from 'utils/sanity/sanity';

interface ArticlePageProps {
    article?: Article;
    moreArticles?: Article[];
    relatedArticles?: Article[];
    preview: boolean;
}

const SingleArticlePage: NextPage<ArticlePageProps> = ({ article, moreArticles, relatedArticles, preview }) => {
    const router = useRouter();

    if (!router.isFallback && !article?.slug) {
        return <ErrorPage statusCode={404} />;
    }

    const { data: previewArticle = {} } = usePreviewSubscription(Q_POST_AND_MORE_POSTS_1, {
        params: { slug: article?.slug },
        initialData: article,
        enabled: preview,
    });
    return (
        <>
            {(previewArticle?.[0] || article) && (
                <>
                    <Meta
                        tags={genJournalMetaData(previewArticle?.[0] || article)}
                        structuredData={schemaMarkupGenerator({
                            data: previewArticle?.[0] || article,
                            schemaType: 'Article',
                        })}
                    />
                    <JournalPage article={previewArticle?.[0] || article} relatedArticles={relatedArticles} />
                </>
            )}
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({ params, preview = false, previewData }) => {
    const data = params?.slug ? await getArticleAndMoreArticles(handleNestedSlugs(params?.slug), preview) : null;
    return {
        props: {
            preview,
            article: data?.post || null,
            moreArticles: data?.moreArticles || null,
            relatedArticles: data?.relatedArticles || null,
        },
        revalidate: 60 * 60,
    };
};

export const getStaticPaths: GetStaticPaths = async ({ defaultLocale, locales }) => {
    const allArticles = await getAllArticlesWithSlug();
    return {
        paths:
            allArticles?.map((post) => ({
                params: {
                    slug: post.slug,
                },
            })) || [],
        fallback: true,
    };
};

const SingleArticlePageWithApollo = withApollo(SingleArticlePage);
SingleArticlePageWithApollo.getLayout = getLayout;
export default SingleArticlePageWithApollo;
